import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Person,
  Phone,
  Telegram,
  AccountBalanceWallet,
  SportsEsports,
  TrendingUp,
  CalendarToday,
  Code,
} from '@mui/icons-material';
import { CompleteUser } from '../../types';
import dayjs from 'dayjs';

interface UserDetailsModalProps {
  user: CompleteUser | null;
  open: boolean;
  onClose: () => void;
  onWalletUpdate: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  user,
  open,
  onClose,
  onWalletUpdate,
}) => {
  if (!user) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'default';
    return status === 'active' ? 'success' : 'error';
  };

  const getStatusLabel = (status?: string) => {
    if (!status) return 'Unknown';
    return status === 'active' ? 'Active' : 'Inactive';
  };

  const calculateWinRate = () => {
    if (user.gamesPlayed === 0) return 0;
    return ((user.gamesWon / user.gamesPlayed) * 100).toFixed(1);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Person />
          <Typography variant="h6">User Details</Typography>
          <Chip
            label={getStatusLabel(user.status)}
            color={getStatusColor(user.status) as any}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Information and Wallet */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 400px', minWidth: { xs: '100%', sm: 280 } }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Person sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1" fontWeight="bold">
                      {user.username}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {user.phoneNumber || 'Not provided'}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Telegram sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {user.telegramUsername || 'Not provided'}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Code sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      Referral: {user.referralCode}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      Joined: {dayjs(user.createdAt).format('MMM DD, YYYY HH:mm')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 400px', minWidth: { xs: '100%', sm: 280 } }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Wallet Information
                  </Typography>
                  <Box display="flex" alignItems="center" mb={2}>
                    <AccountBalanceWallet sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(user.wallet)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onWalletUpdate}
                    fullWidth
                  >
                    Update Wallet
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Game Statistics */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Game Statistics
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px', minWidth: 200, textAlign: 'center' }}>
                  <SportsEsports sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h6">{user.gamesPlayed}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Games Played
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 200, textAlign: 'center' }}>
                  <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />
                  <Typography variant="h6">{user.gamesWon}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Games Won
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 200, textAlign: 'center' }}>
                  <Typography variant="h6">{calculateWinRate()}%</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Win Rate
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 200, textAlign: 'center' }}>
                  <Typography variant="h6">{user.consecutiveWins}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Consecutive Wins
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Game Stats by Stake */}
          {user.gameStats && Object.keys(user.gameStats).length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Game Statistics by Stake
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Stake Amount</TableCell>
                        <TableCell align="right">Games Played</TableCell>
                        <TableCell align="right">Games Won</TableCell>
                        <TableCell align="right">Total Stake</TableCell>
                        <TableCell align="right">Total Winnings</TableCell>
                        <TableCell align="right">Win Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {user.gameStats && Object.entries(user.gameStats).map(([stake, stats]) => (
                        <TableRow key={stake}>
                          <TableCell>{formatCurrency(parseInt(stake))}</TableCell>
                          <TableCell align="right">{stats.played}</TableCell>
                          <TableCell align="right">{stats.won}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(stats.totalStake)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(stats.totalWinnings)}
                          </TableCell>
                          <TableCell align="right">
                            {stats.played > 0
                              ? `${((stats.won / stats.played) * 100).toFixed(1)}%`
                              : '0%'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!user.gameStats || Object.keys(user.gameStats).length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No game statistics available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Additional Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                  <Typography variant="body2" color="textSecondary">
                    Has Made Deposits:
                  </Typography>
                  <Chip
                    label={user.isDeposit ? 'Yes' : 'No'}
                    color={user.isDeposit ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                  <Typography variant="body2" color="textSecondary">
                    Last Win Date:
                  </Typography>
                  <Typography variant="body2">
                    {user.lastWinDate
                      ? dayjs(user.lastWinDate).format('MMM DD, YYYY')
                      : 'No wins yet'}
                  </Typography>
                </Box>
                {user.lastUsernameChange && (
                  <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                    <Typography variant="body2" color="textSecondary">
                      Last Username Change:
                    </Typography>
                    <Typography variant="body2">
                      {dayjs(user.lastUsernameChange).format('MMM DD, YYYY')}
                    </Typography>
                  </Box>
                )}
                {user.lastActivity && (
                  <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                    <Typography variant="body2" color="textSecondary">
                      Last Activity:
                    </Typography>
                    <Typography variant="body2">
                      {dayjs(user.lastActivity).format('MMM DD, YYYY HH:mm')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailsModal; 